import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import {
    catchError,
    distinctUntilChanged,
    first,
    firstValueFrom,
    from,
    map,
    Observable,
    of,
    Subject,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs';

import {
    selectSeReportImagesPerRowInPlaceholder,
    selectSeReportIsPreview,
    selectSeReportIsReadonly,
    selectSeReportKeyImages,
} from '../../../report/report.selectors';
import { KeyImage } from '../../../services/imaging/common';
import { SeSideComponentBase, SeSideComponentType } from '../se-side-components-models';
import {
    ISeSideExaminationStudy,
    ISeSideKeyObject,
    ISeSideKeyObjectImage,
    SeSideImagesType,
} from './se-side-key-images-models';
import { KeyImagesService } from './key-images.service';
import { seReportSetIsModified } from '../../../report/report.actions';
import {
    ModalDialogNotificationService,
    NotificationService,
    NotificationType,
} from '../../../services/notification.service';
import { ButtonClick } from '../../../dialogs/se-modalwindow/se-modalwindow.component';
import { RootState } from '../../../root-store/root.state';
import { tapAlways } from '../../../utils/rxjs-operators/always';

function areKeyImagesEqual(prev: KeyImage[], curr: KeyImage[]): boolean {
    if (prev.length !== curr.length) {
        return false;
    }
    return prev.every((prevImg, index) => {
        const currImg = curr[index];
        return (
            prevImg.SOPInstanceUID === currImg.SOPInstanceUID &&
            prevImg.IdExamination === currImg.IdExamination &&
            prevImg.RepositoryKey === currImg.RepositoryKey
        );
    });
}

@Component({
    selector: 'se-side-key-images-container',
    templateUrl: './se-side-key-images-container.component.html',
    styleUrls: ['./se-side-key-images-container.component.scss'],
    standalone: false,
})
export class SeSideKeyImagesContainerComponent extends SeSideComponentBase implements OnInit, OnDestroy {
    isPreview = false;
    layoutNumber = 2;
    keyImages: ISeSideKeyObjectImage[] = [];
    sideKeyImagesMessage: string | null = null;
    type = SeSideComponentType.keyImagesPreview;

    readonly isReadonly$ = this.store.pipe(select(selectSeReportIsReadonly));

    private ngUnsubscribe = new Subject<void>();

    @Input() examinationStudies: ISeSideExaminationStudy[] = [];

    constructor(
        private keyImagesService: KeyImagesService,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private translateService: TranslateService,
        private notificationService: NotificationService,
        private store: Store<RootState>,
        private cdr: ChangeDetectorRef,
    ) {
        super();
        this.sideKeyImagesMessage = this.translateService.instant('NoResults');
        this.isLoading.next(false);
    }

    ngOnInit(): void {
        this.listenForStoreEvents();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    updateState(event: KeyImage) {
        const convertedObj: KeyImage[] = this.keyImages.map(({ Image: _, ...rest }) => rest);

        let updatedObj: KeyImage[] = convertedObj.map((obj) =>
            obj.IdExamination === event.IdExamination &&
            obj.RepositoryKey === event.RepositoryKey &&
            obj.SOPInstanceUID === event.SOPInstanceUID
                ? event
                : obj,
        );

        this.keyImagesService.saveKeyImagesData(updatedObj);
    }

    showModal(image: ISeSideKeyObjectImage) {
        this.modalDialogNotificationService
            .notify({
                message: this.translateService.instant('ImageKey_DeleteConfirmationMessage'),
                title: this.translateService.instant('Warning'),
                type: NotificationType.information,
                buttonYes: true,
                buttonNo: true,
                buttonOk: false,
                buttonCancel: false,
            })
            .pipe(first())
            .subscribe((button) => {
                if (button === ButtonClick.yes) {
                    this.keyImagesService.requestKeyImageDeletion(image);
                }
            });
    }

    deleteImagesFromRepository() {
        this.modalDialogNotificationService
            .notify({
                message: this.translateService.instant('ImageKey_DeleteAllConfirmationMessage'),
                title: this.translateService.instant('Warning'),
                type: NotificationType.information,
                buttonYes: true,
                buttonNo: true,
                buttonOk: false,
                buttonCancel: false,
            })
            .pipe(first())
            .subscribe((button) => {
                if (button === ButtonClick.yes) {
                    this.keyImagesService.requestAllKeyImagesDeletion(this.keyImages);
                }
            });
    }

    selectRow(number: number) {
        this.keyImagesService.saveRowValue(number);
        this.store.dispatch(seReportSetIsModified({ isModified: true }));
    }

    private listenForStoreEvents() {
        this.store
            .pipe(takeUntil(this.ngUnsubscribe), select(selectSeReportIsPreview))
            .subscribe((isPreview: boolean) => {
                this.isPreview = isPreview;
            });

        this.store
            .pipe(
                takeUntil(this.ngUnsubscribe),
                select(selectSeReportKeyImages),
                distinctUntilChanged(areKeyImagesEqual),
                tap(() => this.isLoading.next(true)),
                switchMap((metaData) => from(this.onKeyImagesChange(metaData))),
                tapAlways(() => this.isLoading.next(false)),
            )
            .subscribe((imgs) => {
                this.keyImages = imgs;
                this.cdr.markForCheck();
                // verifica la coerenza tra componente e store
                this.assertKeyImagesConsistency();
            });

        this.store
            .pipe(takeUntil(this.ngUnsubscribe), select(selectSeReportImagesPerRowInPlaceholder))
            .subscribe((n) => (this.layoutNumber = n));
    }

    /**
     * Verifica che le keyImages nel componente corrispondano a quelle nello store.
     * Genera un warning se c'è una mancata corrispondenza tra le due.
     */
    private assertKeyImagesConsistency(): void {
        this.store
            .pipe(
                select(selectSeReportKeyImages),
                first(),
            )
            .subscribe((storeKeyImages: KeyImage[]) => {
                const componentKeyImages: KeyImage[] = this.keyImages.map(({ Image: _, ...rest }) => rest);

                if (!areKeyImagesEqual(storeKeyImages, componentKeyImages)) {
                    console.warn(
                        '[SeSideKeyImagesContainer] KeyImages consistency mismatch detected!',
                        '\nStore keyImages count:', storeKeyImages.length,
                        '\nComponent keyImages count:', componentKeyImages.length,
                        '\nStore SOPInstanceUIDs:', storeKeyImages.map(k => k.SOPInstanceUID),
                        '\nComponent SOPInstanceUIDs:', componentKeyImages.map(k => k.SOPInstanceUID),
                    );
                }
            });
    }

    private async onKeyImagesChange(metaData: KeyImage[]): Promise<ISeSideKeyObjectImage[]> {
        const notAvailableMsg = this.translateService.instant('KeyImagesServiceNotAvailable');
        const noResultMsg = this.translateService.instant('NoResults');

        if (this.isPreview) {
            return [(await import('./mock-data.json')).default];
        }

        // ci sono studi con almeno uno studyInstanceUID
        const hasValidStudies = this.examinationStudies.some(
            (study) => study.studyInstanceUIDs && study.studyInstanceUIDs.length > 0,
        );

        // se non ci sono metadati o studi validi
        if ((!metaData || metaData.length === 0) && !hasValidStudies) {
            this.sideKeyImagesMessage = noResultMsg;
            return [];
        }

        return firstValueFrom(
            this.resolveKeyImagesServiceCall(metaData, this.examinationStudies, noResultMsg, notAvailableMsg),
        );
    }

    private resolveKeyImagesServiceCall(
        metaData: KeyImage[],
        examinationStudies: ISeSideExaminationStudy[],
        noResultMsg: string,
        notAvailableMsg: string,
    ): Observable<ISeSideKeyObjectImage[]> {
        const request: ISeSideKeyObject[] = metaData.map((elem) => ({
            RepositoryKey: elem.RepositoryKey,
            SOPInstanceUID: elem.SOPInstanceUID,
            IdExamination: elem.IdExamination,
        }));

        return this.keyImagesService.getKeyImages(request, examinationStudies).pipe(
            takeUntil(this.ngUnsubscribe),
            map((images: SeSideImagesType[]) => {
                this.sideKeyImagesMessage = null;
                const result = this.populateObject(images, metaData);

                // FIX: Effettua il merge delle keyImages dall'API con i metaData dallo store
                // Se l'API ha restituito più immagini rispetto a quelle presenti nei metaData, aggiorna lo store con la lista mergiata
                if (result.length > 0) {
                    const mergedKeyImages: KeyImage[] = result.map(({ Image: _, ...rest }) => rest);
                    const metaKeySet = new Set(metaData.map(m => m.RepositoryKey));
                    const hasNewImages = mergedKeyImages.some(img => !metaKeySet.has(img.RepositoryKey));

                    if (hasNewImages || metaData.length !== mergedKeyImages.length) {
                        this.keyImagesService.saveKeyImagesData(mergedKeyImages);
                    }
                }

                return result;
            }),
            catchError((error) => {
                this.sideKeyImagesMessage = noResultMsg;
                this.notificationService.warning(error.statusText ? error.statusText : error.message, notAvailableMsg);

                return of([] as ISeSideKeyObjectImage[]);
            }),
        );
    }

    private populateObject(images: SeSideImagesType[], metaData: KeyImage[]): ISeSideKeyObjectImage[] {
        if (!images) {
            return [];
        }

        return images
            .map((image) => {
                const data = this.getDataByObjectKey(image, metaData);

                if (!data) {
                    return null;
                }

                return {
                    data,
                    image,
                };
            })
            .filter((a) => !!a)
            .map(({ data, image }) => {

                return {
                    Image: image.data,
                    Caption: data.Caption,
                    ExcludedFromReport: data.ExcludedFromReport,
                    IdExamination: data.IdExamination,
                    RepositoryKey: data.RepositoryKey,
                    SOPInstanceUID: data.SOPInstanceUID,
                    IsDeletable: data.IsDeletable,
                };
            });
    }

    private getDataByObjectKey(image: SeSideImagesType, metaData: KeyImage[]): KeyImage | undefined {
        // Prima, cerca un'immagine corrispondente nei metaData (per preservare Caption, ExcludedFromReport, ecc.)
        const matchingMetaData = metaData.find(
            (img) =>
                img.IdExamination === image.idExamination &&
                img.RepositoryKey === image.repositoryKey &&
                img.SOPInstanceUID === image.sopInstanceUID,
        );

        if (matchingMetaData) {
            // Trovato nei metaData - restituisce la versione dei metaData (preserva le modifiche utente come Caption)
            return matchingMetaData;
        }

        // Non trovato nei metaData - questa è una nuova immagine dal repository
        // Restituisce un nuovo oggetto KeyImage così viene inclusa nella visualizzazione
        return {
            SOPInstanceUID: image.sopInstanceUID,
            RepositoryKey: image.repositoryKey,
            IdExamination: image.idExamination,
            Caption: '',
            ExcludedFromReport: false,
            IsDeletable: true, // a true perche arrivano dal repository e non dal pacs
        };
    }
}
